import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  formaterDate,
  formaterIdentite,
  formaterQuantite,
  montant,
  somme,
  type CategorieOpcvm,
} from '@gsm/partage';
import { useRessource, messageErreur } from '../api/ressource';
import { api, ErreurApi } from '../api/client';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille, type TonPastille } from '../composants/Pastille';
import { Nombre } from '../composants/Nombre';
import { Icone } from '../composants/Icone';
import { LienFiche } from '../composants/LienAction';
import { ModaleFonds } from '../composants/ModaleFonds';
import { statutOrdre } from '../composants/statutOrdre';
import { LIBELLE_CAMPAGNE, LIBELLE_ORIGINE, TON_CAMPAGNE } from './Campagnes';

interface AlerteFigee {
  code: string;
  message: string;
  montant: string | null;
}

interface LigneMandat {
  id: string;
  mandatId: string;
  statut: 'INCLUS' | 'EXCLU' | 'SANS_OBJET';
  nbOrdres: number;
  montantAchat: string;
  montantVente: string;
  motifExclusion: string | null;
  alertes: AlerteFigee[] | null;
  mandat: {
    reference: string;
    client: { nom: string; prenom: string };
  };
}

interface Campagne {
  id: string;
  reference: string;
  origine: 'CHANGEMENT_ALLOCATION' | 'REBALANCING' | 'INVESTISSEMENT';
  statut: 'OUVERTE' | 'VALIDEE' | 'ANNULEE';
  dateReference: string;
  commentaire: string | null;
  creePar: string;
  profil: { code: string; libelle: string } | null;
  mandats: LigneMandat[];
}

/** Ordre de la campagne, tel que le rend `GET /ordres/campagne/:id`. */
interface OrdreCampagne {
  id: string;
  reference: string;
  canal: string;
  sens: string;
  mode: string;
  statut: string;
  quantiteDemandee: string | null;
  montantDemande: string | null;
  montantEstime: string;
  quantiteRealisee: string | null;
  montantRealise: string | null;
  instrumentConfirme: boolean;
  instrumentId: string | null;
  mandat: { id: string; reference: string; profil: { charia: boolean } };
  instrument: { libelle: string; opcvm: { categorie: CategorieOpcvm } | null } | null;
  tranche: { libelle: string } | null;
}

const TON_LIGNE: Record<LigneMandat['statut'], TonPastille> = {
  INCLUS: 'interface',
  EXCLU: 'neutre',
  SANS_OBJET: 'neutre',
};

const LIBELLE_LIGNE: Record<LigneMandat['statut'], string> = {
  INCLUS: 'Inclus',
  EXCLU: 'Exclu',
  SANS_OBJET: 'Sans objet',
};

export function FicheCampagne() {
  const { id } = useParams<{ id: string }>();
  const { aRole } = useAuth();
  const { donnees, chargement, erreur, recharger } = useRessource<Campagne>(`/campagnes/${id}`);
  const listeOrdres = useRessource<OrdreCampagne[]>(`/ordres/campagne/${id}`);
  const [substitution, setSubstitution] = useState<OrdreCampagne | null>(null);

  const [action, setAction] = useState<string | null>(null);
  const [erreurAction, setErreurAction] = useState<string | null>(null);

  const c = donnees;
  const peutGerer = aRole('GERANT', 'RESP_GESTION', 'ADMIN');
  const peutValider = aRole('RESP_GESTION');

  async function agir(nom: string, appel: () => Promise<unknown>) {
    setErreurAction(null);
    setAction(nom);
    try {
      await appel();
      await Promise.all([recharger(), listeOrdres.recharger()]);
    } catch (e) {
      setErreurAction(e instanceof ErreurApi ? messageErreur(e) : 'Opération impossible.');
    } finally {
      setAction(null);
    }
  }

  if (chargement) return <p className="surtitre">Chargement…</p>;
  if (erreur || !c)
    return (
      <BandeauAlerte nature="refus" titre="Campagne introuvable">
        {messageErreur(erreur) ?? 'Cette campagne n’existe pas.'}
      </BandeauAlerte>
    );

  const inclus = c.mandats.filter((m) => m.statut === 'INCLUS');
  const totalAchat = somme(inclus.map((m) => montant(m.montantAchat)));
  const totalVente = somme(inclus.map((m) => montant(m.montantVente)));
  const totalOrdres = inclus.reduce((s, m) => s + m.nbOrdres, 0);
  const ouverte = c.statut === 'OUVERTE';

  // Par mandat, puis dans l'ordre de génération.
  const ordres = [...(listeOrdres.donnees ?? [])].sort(
    (a, b) =>
      a.mandat.reference.localeCompare(b.mandat.reference) ||
      a.reference.localeCompare(b.reference),
  );
  const fondsAConfirmer = ordres.filter(
    (o) => o.canal === 'OPCVM' && !o.instrumentConfirme && o.statut === 'BROUILLON',
  ).length;

  const colonnesOrdres: Colonne<OrdreCampagne>[] = [
    { cle: 'reference', entete: 'Référence', num: true, rendu: (o) => o.reference },
    {
      cle: 'mandat',
      entete: 'Mandat',
      num: true,
      rendu: (o) => <LienFiche to={`/mandats/${o.mandat.id}`}>{o.mandat.reference}</LienFiche>,
    },
    {
      cle: 'sens',
      entete: 'Sens',
      rendu: (o) => (
        <span className={o.sens === 'ACHAT' ? 'ordre-achat' : 'ordre-vente'}>{o.sens}</span>
      ),
    },
    { cle: 'canal', entete: 'Canal', rendu: (o) => o.canal.replace('_', ' ').toLowerCase() },
    {
      cle: 'sousjacent',
      entete: 'Sous-jacent',
      rendu: (o) => (
        <>
          {o.instrument?.libelle ?? `BDT · ${o.tranche?.libelle ?? '—'}`}
          {o.canal === 'OPCVM' && !o.instrumentConfirme && o.statut === 'BROUILLON' && (
            <>
              {' '}
              <Pastille ton="attente">Fonds à confirmer</Pastille>
            </>
          )}
          {peutGerer && ouverte && o.canal === 'OPCVM' && o.statut === 'BROUILLON' && (
            <>
              {' '}
              <button
                type="button"
                className="bouton bouton--compact bouton--fantome"
                onClick={() => setSubstitution(o)}
              >
                {o.instrumentConfirme ? 'Changer' : 'Choisir'}
              </button>
            </>
          )}
        </>
      ),
    },
    {
      cle: 'quantite',
      entete: 'Quantité / montant',
      num: true,
      rendu: (o) =>
        o.quantiteDemandee ? (
          formaterQuantite(o.quantiteDemandee)
        ) : (
          <Nombre valeur={o.montantDemande} />
        ),
    },
    {
      cle: 'montant',
      entete: 'Montant estimé',
      num: true,
      rendu: (o) => <Nombre valeur={o.montantEstime} />,
    },
    {
      cle: 'statut',
      entete: 'Statut',
      rendu: (o) => <Pastille ton={statutOrdre(o).ton}>{statutOrdre(o).libelle}</Pastille>,
    },
  ];

  const colonnes: Colonne<LigneMandat>[] = [
    {
      cle: 'mandat',
      entete: 'Mandat',
      num: true,
      rendu: (m) => <LienFiche to={`/mandats/${m.mandatId}`}>{m.mandat.reference}</LienFiche>,
    },
    {
      cle: 'client',
      entete: 'Client',
      rendu: (m) => formaterIdentite(m.mandat.client.nom, m.mandat.client.prenom),
    },
    {
      cle: 'statut',
      entete: 'État',
      rendu: (m) => <Pastille ton={TON_LIGNE[m.statut]}>{LIBELLE_LIGNE[m.statut]}</Pastille>,
    },
    { cle: 'ordres', entete: 'Ordres', num: true, rendu: (m) => m.nbOrdres || '—' },
    {
      cle: 'achat',
      entete: 'Acheté',
      num: true,
      rendu: (m) => (montant(m.montantAchat).gt(0) ? <Nombre valeur={m.montantAchat} /> : '—'),
    },
    {
      cle: 'vente',
      entete: 'Vendu',
      num: true,
      rendu: (m) => (montant(m.montantVente).gt(0) ? <Nombre valeur={m.montantVente} /> : '—'),
    },
    {
      cle: 'alertes',
      entete: 'Alertes',
      rendu: (m) =>
        m.alertes && m.alertes.length > 0 ? (
          <span title={m.alertes.map((a) => a.message).join('\n')}>
            <Pastille ton="attente">{`${m.alertes.length} alerte${m.alertes.length > 1 ? 's' : ''}`}</Pastille>
          </span>
        ) : m.motifExclusion ? (
          <span className="mention">{m.motifExclusion}</span>
        ) : (
          '—'
        ),
    },
    ...(peutGerer
      ? [
          {
            cle: 'actions',
            entete: '',
            num: true,
            rendu: (m: LigneMandat) =>
              ouverte && m.statut === 'INCLUS' ? (
                <button
                  type="button"
                  className="bouton bouton--fantome bouton--compact"
                  disabled={action !== null}
                  onClick={() =>
                    void agir(`exclure-${m.mandatId}`, () =>
                      api.post(`/campagnes/${c.id}/exclure`, { mandatId: m.mandatId }),
                    )
                  }
                >
                  Exclure
                </button>
              ) : (
                '—'
              ),
          },
        ]
      : []),
  ];

  return (
    <>
      <p className="entete__fil">
        <Link to="/campagnes">Campagnes</Link> › {c.reference}
      </p>
      <div className="entete">
        <h1>{LIBELLE_ORIGINE[c.origine]}</h1>
        <span className="entete__ref">{c.reference}</span>
        <Pastille ton={TON_CAMPAGNE[c.statut]}>{LIBELLE_CAMPAGNE[c.statut]}</Pastille>
        {c.profil && <Pastille ton="neutre">{c.profil.libelle}</Pastille>}
      </div>

      <section className="cles" aria-label="Récapitulatif">
        <div className="cle">
          <span className="surtitre">Mandats inclus</span>
          <div className="cle__valeur">
            {inclus.length}
            <small>/ {c.mandats.length}</small>
          </div>
        </div>
        <div className="cle">
          <span className="surtitre">Ordres générés</span>
          <div className="cle__valeur">{totalOrdres}</div>
        </div>
        <div className="cle">
          <span className="surtitre">À acheter</span>
          <div className="cle__valeur">
            <Nombre valeur={totalAchat} />
            <small>MAD</small>
          </div>
        </div>
        <div className="cle">
          <span className="surtitre">À vendre</span>
          <div className="cle__valeur">
            <Nombre valeur={totalVente} />
            <small>MAD</small>
          </div>
        </div>
        <div className="cle">
          <span className="surtitre">Date de référence</span>
          <div className="cle__valeur">{formaterDate(c.dateReference)}</div>
        </div>
      </section>

      {erreurAction && (
        <BandeauAlerte nature="refus" titre="Opération refusée">
          {erreurAction}
        </BandeauAlerte>
      )}

      {/* Une donnée de la campagne, pas une alerte : elle se lit comme le reste. */}
      {c.commentaire && (
        <p className="note note--tete">
          <span className="surtitre">Commentaire</span> {c.commentaire}
        </p>
      )}

      <Bloc titre="Mandats" meta={`${c.mandats.length}`}>
        <Tableau colonnes={colonnes} lignes={c.mandats} cle={(m) => m.id} vide="Aucun mandat." />
      </Bloc>

      {ouverte && fondsAConfirmer > 0 && (
        <BandeauAlerte nature="gestion" titre="Fonds OPCVM à confirmer">
          {fondsAConfirmer} ordre(s) portent le fonds proposé par défaut. La campagne ne peut pas
          être validée tant que le gérant ne les a pas confirmés (RG-16).
        </BandeauAlerte>
      )}

      <Bloc titre="Ordres" meta={listeOrdres.chargement ? 'chargement…' : `${ordres.length}`}>
        {listeOrdres.erreur && (
          <BandeauAlerte nature="refus" titre="Chargement impossible">
            {messageErreur(listeOrdres.erreur)}
          </BandeauAlerte>
        )}
        <Tableau
          colonnes={colonnesOrdres}
          lignes={ordres}
          cle={(o) => o.id}
          vide="Aucun ordre dans cette campagne."
        />
      </Bloc>

      <Bloc titre="Validation">
        <div className="barre-actions">
          {ouverte && peutValider && (
            <button
              className="bouton bouton--icone"
              disabled={action !== null || inclus.length === 0}
              onClick={() => void agir('valider', () => api.post(`/campagnes/${c.id}/valider`))}
            >
              <Icone nom="campagnes" taille={15} />
              Valider et transmettre aux paniers
            </button>
          )}
          {!ouverte && (
            <p className="consigne">
              Campagne {LIBELLE_CAMPAGNE[c.statut].toLowerCase()} — les ordres inclus ont rejoint le
              circuit des paniers.
            </p>
          )}
          {ouverte && (
            <p className="consigne">
              La validation soumet puis valide les ordres des mandats inclus (RG-10 : un autre que
              l’auteur). Les fonds OPCVM doivent d’abord être confirmés, depuis la liste des ordres.
            </p>
          )}
        </div>
      </Bloc>

      <ModaleFonds
        ordre={substitution}
        onFermer={() => setSubstitution(null)}
        onConfirme={async () => {
          setSubstitution(null);
          await listeOrdres.recharger();
        }}
      />
    </>
  );
}
